import React, { useState, useEffect } from 'react';
import { Form, Input, Select, AutoComplete, Button, Row, Col } from 'antd';
import CUSTOMER_CATEGORY_OPTIONS from '../../../config/customer-categories';

import api from '../../../services/api';
import userService from '../../../services/user';
const { Option } = Select;

interface ClientInfoTabProps {
  form: any;
  isEdit?: boolean;
  getReadOnlyProps?: (type: 'input' | 'select') => object;
  /** 在外部分组显示渠道字段时，隐藏本组件内的渠道 */
  hideChannel?: boolean;
}

/**
 * 客户信息 Tab
 */
const ClientInfoSection: React.FC<ClientInfoTabProps> = ({ form, isEdit = false, getReadOnlyProps: injectedGetReadOnlyProps, hideChannel = false }) => {

  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
  const [userOptions, setUserOptions] = useState<Array<{ value: string; label: string; data: any }>>([]);
  const [isAutoFilledUser, setIsAutoFilledUser] = useState(false);

  // 不在本组件内创建用户

  // 使用父级传入的表单实例，保持整体表单一致


  // 处理客户对接人输入变化
  const handleClientContactSearch = (value: string) => {
    const contactName = value.trim();

    // 手动输入时重置自动填充状态
    if (isAutoFilledUser) {
      setIsAutoFilledUser(false);
    }

    // 清除之前的定时器
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    // 清空当前选项
    if (contactName.length < 2) {
      setUserOptions([]);
      return;
    }

    // 至少输入2个字符才搜索
    const timeout = setTimeout(() => {
      searchUsers(contactName);
    }, 500); // 延迟500ms搜索
    setSearchTimeout(timeout);
  };

  // 不在本组件自动创建用户；提交时由外层页面统一处理

  // 获取只读样式的helper函数
  const defaultGetReadOnlyProps = (fieldType: 'input' | 'select' = 'input') => {
    // 如果是自动填充的用户，或者在编辑模式下已关联用户，设置为只读
    const shouldReadOnly = isAutoFilledUser || (isEdit && form.getFieldValue('client_id'));

    if (fieldType === 'select') {
      return {
        disabled: shouldReadOnly
      };
    } else {
      return {
        readOnly: shouldReadOnly,
        style: shouldReadOnly ? { backgroundColor: '#f5f5f5', color: '#999' } : {}
      };
    }
  };
  const getReadOnlyProps = injectedGetReadOnlyProps || defaultGetReadOnlyProps;

  // 处理用户选择
  const handleUserSelect = async (value: string, option: any) => {
    console.log('用户选择事件触发:', { value, option });
    if (option.data) {
      const user = option.data;
      await fillFormWithUserData(user);
      // 将选中的用户ID写入父级表单，便于跨 Tab/重渲染维持只读状态
      const clientId = user.id;
      form.setFieldsValue({ client_id: clientId });
      setIsAutoFilledUser(true); // 标记为自动填充的用户
      console.log('已设置 isAutoFilledUser 为 true');
    }
  };

  // 重置用户选择
  const handleResetUserSelection = () => {
    setIsAutoFilledUser(false);
    // 清空相关字段并移除 client_id，使字段可编辑
      form.setFieldsValue({
        client_id: undefined,
        contact_name: '',
        client_email: '',
        notification_emails: '',
        phone: '',
        customer_categories: [],
        group_number: '',
      });
    setUserOptions([]);
    console.log('已重置用户选择');
  };

  // 搜索用户
  const searchUsers = async (contactName: string) => {
    try {
      // 构建搜索参数 - 基于联系人姓名、邮箱等信息搜索
      const searchParams = new URLSearchParams({
        search: contactName,
        fields: 'name,email,client_type,channel,source'
      });

      const response = await api.get(`/users/search?${searchParams}`);

      if (response.data && response.data.success && response.data.data.length > 0) {
        // 设置下拉选项
        const options = response.data.data.map((user: any) => ({
          value: user.name || user.contact_name,
          label: `${user.name || user.contact_name} (${user.email})`,
          data: user
        }));
        setUserOptions(options);
      } else {
        setUserOptions([]);
      }
    } catch (error) {
      console.error('搜索用户失败:', error);
      setUserOptions([]);
    }
  };

  // 使用用户数据填充表单
  const fillFormWithUserData = async (user: any) => {
    try {
      console.log('开始填充用户数据:', user);

      // 自动填充其他相关字段
      const fieldsToUpdate: any = {};

      if (user.email) {
        fieldsToUpdate.client_email = user.email;
        console.log('填充邮箱:', user.email);
      }

      if (user.client_role) {
        fieldsToUpdate.client_role = user.client_role;
        console.log('填充客户角色:', user.client_role);
      }

      // 填充客户等级（现在在财务信息中，但仍需要在这里填充）
      if (user.client_type) {
        fieldsToUpdate.client_type = user.client_type;
        console.log('填充客户等级:', user.client_type);
      }

      // 填充客户来源（现在在财务信息中，但仍需要在这里填充）
      if (user.source) {
        fieldsToUpdate.source = user.source;
        console.log('填充来源:', user.source);
      }

      if ((user as any).customer_categories && !fieldsToUpdate.customer_categories) {
        const raw = (user as any).customer_categories;
        if (Array.isArray(raw)) {
          fieldsToUpdate.customer_categories = raw;
        } else if (typeof raw === 'string') {
          try {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed)) {
              fieldsToUpdate.customer_categories = parsed;
            }
          } catch {
            const list = raw.split(',').map((s: string) => s.trim()).filter(Boolean);
            if (list.length) fieldsToUpdate.customer_categories = list;
          }
        }
      }

      if (user.contact_name) {
        fieldsToUpdate.contact_name = user.contact_name;
        console.log('填充联系人姓名:', user.contact_name);
      } else if ((user as any).display_name || (user as any).username || user.name) {
        const dn = (user as any).display_name || (user as any).username || user.name;
        fieldsToUpdate.contact_name = dn;
        console.log('填充联系人姓名(用name):', dn);
      }

      // 通知邮箱（从 metas 或用户对象中读取）
      try {
        if ((user as any).notification_emails) {
          fieldsToUpdate.notification_emails = (user as any).notification_emails;
        }
      } catch {}
      // 客户电话：尝试直接使用 user.phone（后端可能提供了访问器）
      if ((user as any).phone && !fieldsToUpdate.phone) {
        fieldsToUpdate.phone = (user as any).phone;
      }
      // 客户群号：尝试直接使用 user.group_number（后端提供访问器）
      if ((user as any).group_number && !fieldsToUpdate.group_number) {
        fieldsToUpdate.group_number = (user as any).group_number;
      }

      console.log('准备填充的字段:', fieldsToUpdate);
      form.setFieldsValue(fieldsToUpdate);
      console.log('已自动填充用户信息:', (user as any).display_name || (user as any).username || user.name || user.contact_name);
    } catch (error) {
      console.error('填充用户数据失败:', error);
    }
  };

  // ✅ 封装客户对接人字段
  const renderClientContactFields = () => (
    <>
      {/* 隐藏字段：确保 client_id 随表单一起提交 */}
      <Form.Item name="client_id" hidden>
        <Input type="hidden" />
      </Form.Item>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label={
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                客户名称
                {(isAutoFilledUser || !!form.getFieldValue('client_id')) && (
                  <Button
                    size="small"
                    type="link"
                    onClick={handleResetUserSelection}
                    style={{ padding: 0, height: 'auto', fontSize: '12px' }}
                  >
                    重新选择
                  </Button>
                )}
              </div>
            }
            name="contact_name"
            rules={[{ required: true, message: '请输入客户名称' }]}
          >
            <AutoComplete
              placeholder="请输入客户名称"
              options={userOptions}
              onSearch={handleClientContactSearch}
              onSelect={handleUserSelect}
              filterOption={false}
              disabled={isAutoFilledUser || !!form.getFieldValue('client_id')}
            />
          </Form.Item>
        </Col>

        <Col span={12}>
          <Form.Item
            label="客户角色"
            name="client_role"
          >
            <Select placeholder="请选择客户角色" allowClear {...getReadOnlyProps('select')}>
              <Option value="peers">同行</Option>
              <Option value="client">直客</Option>
            </Select>
          </Form.Item>
        </Col>

        <Col span={12}>
          <Form.Item
            label="客户邮箱"
            name="client_email"
            rules={[
              { required: true, message: '请输入客户邮箱' },
              { type: 'email', message: '请输入有效的邮箱地址' },
            ]}
          >
            <Input placeholder="请输入客户邮箱" {...getReadOnlyProps('input')} data-testid="order-client-email" />
          </Form.Item>
        </Col>

        <Col span={12}>
          <Form.Item label="客户电话" name="phone">
            <Input placeholder="请输入客户电话" {...getReadOnlyProps('input')} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item label="客户群号" name="group_number">
            <Input placeholder="用于同步沟通的群号（可选）" data-testid="order-group-number" {...getReadOnlyProps('input')} />
          </Form.Item>
        </Col>

        <Col span={12}>
          <Form.Item label="客户分类" name="customer_categories">
            <Select
              mode="multiple"
              allowClear
              showSearch
              placeholder="请选择客户分类（可多选）"
              {...getReadOnlyProps('select')}
            >
              {CUSTOMER_CATEGORY_OPTIONS.map(option => (
                <Option key={option} value={option}>
                  {option}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <Form.Item
        label="通知邮箱"
        name="notification_emails"
        extra="多个邮箱用 ; 分隔开"
      >
        <Input placeholder="例如: a@example.com; b@example.com" {...getReadOnlyProps('input')} />
      </Form.Item>

    </>
  );


  // 在编辑模式或已有关联客户时，将字段设置为只读
  useEffect(() => {
    if (isEdit || form.getFieldValue('client_id')) {
      const clientId = form.getFieldValue('client_id');
      if (clientId) {
        setIsAutoFilledUser(true);
      }
    }
  }, [form, isEdit]);

  // 编辑模式下：若存在 client_id 但缺少 client_type，则自动根据用户资料填充
  useEffect(() => {
    const clientId = form.getFieldValue('client_id');
    const clientType = form.getFieldValue('client_type');
    if (isEdit && clientId && !clientType) {
      (async () => {
        try {
          const res = await userService.getUser(Number(clientId));
          if (res.success && res.data) {
            await fillFormWithUserData(res.data);
            setIsAutoFilledUser(true);
          }
        } catch (e) {
          console.warn('根据 client_id 自动填充用户资料失败:', e);
        }
      })();
    }
  }, [isEdit, form]);

  // 编辑模式下：若存在 client_id，但部分字段（如通知邮箱、电话）为空，则增量补全这些缺失字段
  useEffect(() => {
    const clientId = form.getFieldValue('client_id');
    const needNotify = !form.getFieldValue('notification_emails');
    const needPhone = !form.getFieldValue('phone');
    const needGroupNumber = !form.getFieldValue('group_number');
    const needCategories = !Array.isArray(form.getFieldValue('customer_categories')) || form.getFieldValue('customer_categories').length === 0;
    if (isEdit && clientId && (needNotify || needPhone || needGroupNumber || needCategories)) {
      (async () => {
        try {
          const res = await userService.getUser(Number(clientId));
          if (res.success && res.data) {
            const user = res.data as any;
            const patch: any = {};
            if (needNotify) {
              const notify = (user as any).notification_emails || '';
              if (notify) patch.notification_emails = notify;
            }
            if (needPhone) {
              const phone = (user as any).phone || '';
              if (phone) patch.phone = phone;
            }
            if (needGroupNumber) {
              const groupNumber = (user as any).group_number || '';
              if (groupNumber) patch.group_number = groupNumber;
            }
            if (needCategories) {
              const rawCategories = (user as any).customer_categories || '';
              if (rawCategories) {
                try {
                  const parsed = typeof rawCategories === 'string' ? JSON.parse(rawCategories) : rawCategories;
                  if (Array.isArray(parsed)) {
                    patch.customer_categories = parsed;
                  }
                } catch {
                  const list = String(rawCategories).split(',').map((s) => s.trim()).filter(Boolean);
                  if (list.length) patch.customer_categories = list;
                }
              }
            }
            if (Object.keys(patch).length > 0) {
              form.setFieldsValue(patch);
            }
          }
        } catch (e) {
          // ignore
        }
      })();
    }
  }, [isEdit, form]);

  return <>{renderClientContactFields()}</>;
};

export default ClientInfoSection;
